
import { toast as sonnerToast, ToastT } from "sonner";
import { ReactNode } from "react";

// Define toast types
type ToastType = 'default' | 'success' | 'error' | 'warning' | 'info';

// Custom Toast interface that extends sonner's ToastT type with our own fields
export interface Toast {
  title?: string;
  description?: string;
  variant?: "default" | "destructive" | "success" | "error" | "warning" | "info";
  duration?: number;
  action?: ReactNode;
  cancel?: ReactNode;
}

// Shared notification styling/options by type with shorter durations
const getToastOptions = (type: ToastType, duration?: number) => ({
  duration: duration || (type === 'error' ? 3000 : 2500), // Shorter durations
  style: {
    borderRadius: '16px',
    backdropFilter: 'blur(24px)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1)',
  }
});

// Main toast function
export const toast = (props: Toast) => {
  const { title, description, variant = "default", duration, action, cancel } = props;
  
  return sonnerToast(title || "", {
    description,
    duration: duration || 2500, // Shorter default
    action,
    cancel,
    style: {
      borderRadius: '16px',
      backdropFilter: 'blur(24px)',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1)',
    }
  });
};

// Success notification with modern styling
export const showSuccess = (props: Toast) => {
  const { title, description, duration, ...rest } = props;
  return sonnerToast.success(title || "", {
    description,
    ...getToastOptions('success', duration),
    ...rest
  });
};

// Error notification with modern styling
export const showError = (props: Toast) => {
  const { title, description, duration, ...rest } = props;
  return sonnerToast.error(title || "", {
    description,
    ...getToastOptions('error', duration),
    ...rest
  });
};

// Warning notification with modern styling
export const showWarning = (props: Toast) => {
  const { title, description, duration, ...rest } = props;
  return sonnerToast.warning(title || "", {
    description,
    ...getToastOptions('warning', duration),
    ...rest
  });
};

// Info notification with modern styling
export const showInfo = (props: Toast) => {
  const { title, description, duration, ...rest } = props;
  return sonnerToast.info(title || "", {
    description,
    ...getToastOptions('info', duration),
    ...rest
  });
};

// For backward compatibility with existing code
export const success = showSuccess;
export const error = showError;
export const warning = showWarning;
export const info = showInfo;

// Custom hook for typed toast usage
export const useToast = () => {
  return {
    toast,
    toasts: [],
    success: showSuccess,
    error: showError,
    warning: showWarning,
    info: showInfo,
    dismiss: sonnerToast.dismiss,
  };
};

export default useToast;
